/**
 * A surface: its section strip, and whichever section the address names.
 *
 * The strip lists one tab per legacy step key rather than one per section, so
 * two steps merged into one section are still two named destinations. Phase 9
 * collapses each pair as it rewrites the section into a single component.
 *
 * @module router/programme-editor/SurfaceLayout
 */

import React, { useEffect, useRef } from "react";

import { Outlet, useParams } from "react-router";

import { type LinkTabItem, LinkTabs } from "@/components/ui";
import { useProgramme } from "@/hooks/useStore";
import { isIdentityComplete, state } from "@/state/store";
import { evaluateDevelopmentSetup } from "@/utils/development-setup";

import { applicableStateKeys, lockedReason, type SurfaceDef, unlockedStateKeys } from "./surfaces";

/** The base address of the programme or change case being edited. */
export function useEditorBase(): string {
  const { programmeId, changeCaseId } = useParams<{
    programmeId: string;
    changeCaseId?: string;
  }>();
  return changeCaseId
    ? `/programmes/${programmeId}/changes/${changeCaseId}`
    : `/programmes/${programmeId}`;
}

export function surfaceTabs(surface: SurfaceDef, base: string): readonly LinkTabItem[] {
  const unlocked = unlockedStateKeys();
  // One identity check for the whole strip: every locked tab asks the same
  // question, and answering it reads the whole programme.
  const identityComplete = isIdentityComplete();
  // The steps the locked tabs wait on, so each is marked where it is missing.
  const outstanding = new Set<string>(
    evaluateDevelopmentSetup(state.programme)
      .requirements.filter((requirement) => !requirement.complete)
      .map((requirement) => requirement.key),
  );
  // Identity gates the rest on its validation too, so it counts as outstanding
  // by the same predicate the locks use, not by its fields alone.
  if (!identityComplete) {
    outstanding.add("identity");
  }
  return surface.sections.flatMap<LinkTabItem>((section) => {
    const to = section.segment
      ? `${base}/${surface.segment}/${section.segment}`
      : `${base}/${surface.segment}`;
    // A section with no step behind it is still a page, so it takes one tab
    // under its own title; `end` keeps the index from holding every sibling.
    if (section.stateKeys.length === 0) {
      return [
        {
          to,
          label: section.title,
          end: !section.segment,
          "data-testid": `tab-${surface.segment}-${section.segment || "index"}`,
        },
      ];
    }
    // One tab per section, whatever steps it holds: a second tab for a step
    // sharing the section's page opened the same body twice. The tab is locked
    // only when every step under it is.
    const keys = applicableStateKeys(section.stateKeys);
    const reason = keys.every((key) => !unlocked.has(key))
      ? lockedReason(keys[0] ?? section.stateKeys[0]!, identityComplete)
      : undefined;
    return [
      {
        to,
        label: section.title,
        lockedReason: reason,
        incomplete: keys.some((key) => unlocked.has(key) && outstanding.has(key)),
        "data-testid": `step-${section.stateKeys[0]}`,
      },
    ];
  });
}

export function SurfaceLayout({ surface }: { surface: SurfaceDef }): React.ReactElement {
  // Subscribed, not read: the lock state comes from the legacy store, which
  // nothing else in this subtree observes.
  useProgramme();
  const base = useEditorBase();
  const tabs = surfaceTabs(surface, base);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const bandRef = useRef<HTMLDivElement>(null);

  // The band's height, for a band inside a section to stick below it. Set on
  // this element rather than the root, so it reaches only what sits under it.
  useEffect(() => {
    const band = bandRef.current;
    if (!band || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      surfaceRef.current?.style.setProperty("--sq-surface-band-height", `${band.offsetHeight}px`);
    });
    observer.observe(band);
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={surfaceRef}
      // A section scrolled to by its anchor lands below the bands, not under them.
      className="space-y-4 [&_#content]:scroll-mt-[calc(var(--sq-topbar-height)+var(--sq-surface-band-height)+0.5rem)] [&_[id^=step-]]:scroll-mt-[calc(var(--sq-topbar-height)+var(--sq-surface-band-height)+0.5rem)]"
      data-testid={`surface-${surface.segment}`}
    >
      {/* Sticky, so the section being edited stays named down a long form. */}
      <div
        ref={bandRef}
        className="sticky top-(--sq-topbar-height) z-40 bg-base-100 pt-2"
        data-testid="surface-band"
      >
        <LinkTabs items={tabs} aria-label={`${surface.title} sections`} oneRow />
      </div>
      <Outlet />
    </div>
  );
}
